import React, { useCallback, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  SwapHoriz as DirectIcon,
  Functions as DerivedIcon,
  Analytics as AggregateIcon,
  Tag as ConstantIcon,
} from '@mui/icons-material';
import type { MappingResponse } from '../services/api';

/** The four creation modes */
type CreationMode = 'direct' | 'derived' | 'aggregate' | 'constant';

const AGG_FUNCTIONS = ['COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'COUNT DISTINCT', 'STRING_AGG'] as const;
const DERIVED_FUNCTIONS = ['CONCAT', 'COALESCE', 'CASE WHEN', 'IF', 'REPLACE', 'Custom SQL'] as const;

export interface MappingCreationPanelProps {
  targetTable: string;
  targetField: string;
  mappingResponse?: MappingResponse;
  /** Source options extracted from the mapping response */
  sourceOptions: { files: string[]; fieldsByFile: Record<string, string[]> };
  /** Called to create the mapping */
  onCreate: (
    targetTable: string,
    targetField: string,
    sourceFile?: string,
    sourceField?: string,
    transformation?: string,
    transformationType?: string,
    operation?: string,
    parameters?: Record<string, any>,
  ) => void;
  saving?: boolean;
}

const MappingCreationPanel: React.FC<MappingCreationPanelProps> = ({
  targetTable,
  targetField,
  sourceOptions,
  onCreate,
  saving,
}) => {
  const [mode, setMode] = useState<CreationMode>('direct');

  // Direct mode state
  const [selectedSourceFile, setSelectedSourceFile] = useState<string | null>(null);
  const [selectedSourceField, setSelectedSourceField] = useState<string | null>(null);

  // Derived mode state
  const [derivedSourceFile, setDerivedSourceFile] = useState<string | null>(null);
  const [derivedFields, setDerivedFields] = useState<string[]>([]);
  const [derivedFunction, setDerivedFunction] = useState<string>('CONCAT');
  const [derivedSeparator, setDerivedSeparator] = useState<string>(', ');
  const [derivedCustomExpr, setDerivedCustomExpr] = useState<string>('');

  // Aggregate mode state
  const [aggSourceFile, setAggSourceFile] = useState<string | null>(null);
  const [aggFunction, setAggFunction] = useState<string>('COUNT');
  const [aggField, setAggField] = useState<string | null>(null);
  const [aggGroupBy, setAggGroupBy] = useState<string[]>([]);
  const [aggHaving, setAggHaving] = useState<string>('');

  // Constant mode state
  const [constValue, setConstValue] = useState<string>('');
  const [constType, setConstType] = useState<string>('String');

  const availableSourceFields = useMemo(() => {
    const file =
      mode === 'direct' ? selectedSourceFile
      : mode === 'derived' ? derivedSourceFile
      : mode === 'aggregate' ? aggSourceFile
      : null;
    if (!file) return [];
    return sourceOptions.fieldsByFile[file] ?? [];
  }, [mode, selectedSourceFile, derivedSourceFile, aggSourceFile, sourceOptions]);

  // --- Derived expression builder ---
  const derivedExpression = useMemo(() => {
    if (derivedFunction === 'Custom SQL') return derivedCustomExpr;
    if (derivedFields.length === 0) return '';
    if (derivedFunction === 'CONCAT') {
      return `CONCAT(${derivedFields.map((f) => f).join(`${derivedSeparator ? `, '${derivedSeparator}', ` : ', '}`)})`;
    }
    if (derivedFunction === 'COALESCE') {
      return `COALESCE(${derivedFields.join(', ')})`;
    }
    if (derivedFunction === 'CASE WHEN') {
      if (derivedFields.length >= 2) {
        return `CASE WHEN ${derivedFields[0]} IS NOT NULL THEN ${derivedFields[0]} ELSE ${derivedFields[1]} END`;
      }
      return `CASE WHEN ${derivedFields[0]} IS NOT NULL THEN ${derivedFields[0]} ELSE NULL END`;
    }
    if (derivedFunction === 'IF') {
      if (derivedFields.length >= 2) {
        return `IF(${derivedFields[0]} IS NOT NULL, ${derivedFields[0]}, ${derivedFields[1]})`;
      }
      return `IF(${derivedFields[0]} IS NOT NULL, ${derivedFields[0]}, NULL)`;
    }
    if (derivedFunction === 'REPLACE') {
      return `REPLACE(${derivedFields[0] ?? ''}, '', '')`;
    }
    return derivedFields.join(', ');
  }, [derivedFields, derivedFunction, derivedSeparator, derivedCustomExpr]);

  // --- Aggregate expression builder ---
  const aggExpression = useMemo(() => {
    if (!aggFunction || !aggField) return '';
    const fn = aggFunction === 'COUNT DISTINCT' ? `COUNT(DISTINCT ${aggField})` : `${aggFunction}(${aggField})`;
    let expr = fn;
    if (aggGroupBy.length > 0) {
      expr += ` /* GROUP BY ${aggGroupBy.join(', ')} */`;
    }
    if (aggHaving.trim()) {
      expr += ` /* HAVING ${aggHaving.trim()} */`;
    }
    return expr;
  }, [aggFunction, aggField, aggGroupBy, aggHaving]);

  // --- Constant expression builder ---
  const constExpression = useMemo(() => {
    if (constType === 'NULL') return 'NULL';
    if (constType === 'UUID()') return 'GENERATE_UUID()';
    if (constType === 'NOW()') return 'CURRENT_TIMESTAMP()';
    if (constType === 'Boolean') return constValue.toLowerCase() === 'true' ? 'TRUE' : 'FALSE';
    if (constType === 'Number') return constValue || '0';
    return constValue ? `'${constValue}'` : '';
  }, [constValue, constType]);

  const handleCreate = useCallback(() => {
    switch (mode) {
      case 'direct':
        onCreate(
          targetTable,
          targetField,
          selectedSourceFile ?? undefined,
          selectedSourceField ?? undefined,
        );
        break;
      case 'derived':
        onCreate(
          targetTable,
          targetField,
          derivedSourceFile ?? undefined,
          derivedFields[0] ?? undefined,
          derivedExpression,
          'expression',
        );
        break;
      case 'aggregate':
        onCreate(
          targetTable,
          targetField,
          aggSourceFile ?? undefined,
          aggField ?? undefined,
          aggExpression,
          'aggregation',
          'aggregate',
          {
            function: aggFunction === 'COUNT DISTINCT' ? 'COUNT_DISTINCT' : aggFunction,
            group_by: aggGroupBy,
            ...(aggHaving.trim() ? { having: aggHaving.trim() } : {}),
          },
        );
        break;
      case 'constant':
        onCreate(
          targetTable,
          targetField,
          undefined,
          undefined,
          constExpression,
          'literal',
          'set_constant',
          { value: constExpression },
        );
        break;
    }
  }, [
    mode, targetTable, targetField, selectedSourceFile, selectedSourceField,
    derivedSourceFile, derivedFields, derivedExpression,
    aggSourceFile, aggField, aggExpression,
    constExpression, onCreate,
  ]);

  const canCreate = useMemo(() => {
    if (saving) return false;
    switch (mode) {
      case 'direct': return Boolean(selectedSourceField);
      case 'derived': return derivedExpression.trim().length > 0;
      case 'aggregate': return aggExpression.trim().length > 0;
      case 'constant': return constExpression.trim().length > 0;
    }
  }, [mode, saving, selectedSourceField, derivedExpression, aggExpression, constExpression]);

  const createLabel = useMemo(() => {
    if (saving) return 'Creating...';
    switch (mode) {
      case 'direct':
        return selectedSourceField ? `Map ${selectedSourceField} → ${targetField}` : 'Select a Source Field';
      case 'derived': return 'Create Derived Mapping';
      case 'aggregate': return 'Create Aggregate Mapping';
      case 'constant': return 'Create Constant Mapping';
    }
  }, [mode, saving, selectedSourceField, targetField]);

  return (
    <Stack spacing={2} p={2} textAlign="left">
      <Typography variant="subtitle2" sx={{ mt: 1 }}>
        Unmapped Field: {targetField}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {targetTable}.{targetField} is currently not mapped.
      </Typography>
      <Divider sx={{ width: '100%' }} />

      {/* Mode selector */}
      <ToggleButtonGroup
        value={mode}
        exclusive
        onChange={(_, val) => val && setMode(val as CreationMode)}
        size="small"
        fullWidth
        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 12, py: 0.75 } }}
      >
        <ToggleButton value="direct">
          <DirectIcon sx={{ fontSize: 16, mr: 0.5 }} /> Direct
        </ToggleButton>
        <ToggleButton value="derived">
          <DerivedIcon sx={{ fontSize: 16, mr: 0.5 }} /> Derived
        </ToggleButton>
        <ToggleButton value="aggregate">
          <AggregateIcon sx={{ fontSize: 16, mr: 0.5 }} /> Aggregate
        </ToggleButton>
        <ToggleButton value="constant">
          <ConstantIcon sx={{ fontSize: 16, mr: 0.5 }} /> Constant
        </ToggleButton>
      </ToggleButtonGroup>

      {/* ── Direct Mode ── */}
      {mode === 'direct' && (
        <>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Select a source to map from:
          </Typography>
          <Autocomplete
            size="small"
            options={sourceOptions.files}
            value={selectedSourceFile}
            onChange={(_, value) => {
              setSelectedSourceFile(value);
              setSelectedSourceField(null);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Source File / Table" placeholder="Select source..." />
            )}
          />
          <Autocomplete
            size="small"
            options={availableSourceFields}
            value={selectedSourceField}
            onChange={(_, value) => setSelectedSourceField(value)}
            disabled={!selectedSourceFile}
            renderInput={(params) => (
              <TextField {...params} label="Source Field" required placeholder={selectedSourceFile ? 'Select field...' : 'Select source first'} />
            )}
          />
          {selectedSourceFile && selectedSourceField && (
            <Typography variant="caption" color="primary">
              {selectedSourceFile}.{selectedSourceField} → {targetTable}.{targetField}
            </Typography>
          )}
        </>
      )}

      {/* ── Derived Mode ── */}
      {mode === 'derived' && (
        <>
          <Autocomplete
            size="small"
            options={sourceOptions.files}
            value={derivedSourceFile}
            onChange={(_, value) => {
              setDerivedSourceFile(value);
              setDerivedFields([]);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Source File / Table" placeholder="Select source..." />
            )}
          />
          <Autocomplete
            size="small"
            multiple
            options={availableSourceFields}
            value={derivedFields}
            onChange={(_, value) => setDerivedFields(value)}
            disabled={!derivedSourceFile}
            renderInput={(params) => (
              <TextField {...params} label="Source Fields (multi-select)" placeholder="Select fields..." />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  sx={{ backgroundColor: '#dbeafe', color: '#1e40af', fontWeight: 500, fontSize: 11 }}
                />
              ))
            }
          />
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Function:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {DERIVED_FUNCTIONS.map((fn) => (
              <Chip
                key={fn}
                label={fn}
                size="small"
                variant={derivedFunction === fn ? 'filled' : 'outlined'}
                color={derivedFunction === fn ? 'secondary' : 'default'}
                onClick={() => setDerivedFunction(fn)}
                sx={{ fontSize: 11, cursor: 'pointer' }}
              />
            ))}
          </Box>
          {derivedFunction === 'CONCAT' && (
            <TextField
              size="small"
              label="Separator"
              value={derivedSeparator}
              onChange={(e) => setDerivedSeparator(e.target.value)}
              sx={{ width: 120, '& input': { fontFamily: 'monospace', fontSize: 13 } }}
            />
          )}
          {derivedFunction === 'Custom SQL' && (
            <TextField
              size="small"
              label="Custom Expression"
              value={derivedCustomExpr}
              onChange={(e) => setDerivedCustomExpr(e.target.value)}
              multiline
              minRows={2}
              fullWidth
              sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 12 } }}
            />
          )}
          {derivedExpression && (
            <Box sx={{
              p: 1.5, backgroundColor: '#1e293b', borderRadius: 1,
              fontFamily: 'monospace', fontSize: 12, color: '#e2e8f0', lineHeight: 1.6,
              overflowX: 'auto',
            }}>
              {derivedExpression}
            </Box>
          )}
        </>
      )}

      {/* ── Aggregate Mode ── */}
      {mode === 'aggregate' && (
        <>
          <Autocomplete
            size="small"
            options={sourceOptions.files}
            value={aggSourceFile}
            onChange={(_, value) => {
              setAggSourceFile(value);
              setAggField(null);
              setAggGroupBy([]);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Source Table" placeholder="Select source..." />
            )}
          />
          <Stack direction="row" spacing={1}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Function</InputLabel>
              <Select
                value={aggFunction}
                label="Function"
                onChange={(e) => setAggFunction(e.target.value)}
              >
                {AGG_FUNCTIONS.map((fn) => (
                  <MenuItem key={fn} value={fn}>{fn}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              size="small"
              options={availableSourceFields}
              value={aggField}
              onChange={(_, value) => setAggField(value)}
              disabled={!aggSourceFile}
              sx={{ flex: 1 }}
              renderInput={(params) => (
                <TextField {...params} label="Field to Aggregate" placeholder="Select field..." />
              )}
            />
          </Stack>
          <Autocomplete
            size="small"
            multiple
            options={availableSourceFields}
            value={aggGroupBy}
            onChange={(_, value) => setAggGroupBy(value)}
            disabled={!aggSourceFile}
            renderInput={(params) => (
              <TextField {...params} label="GROUP BY" placeholder="Select group-by fields..." />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  sx={{ backgroundColor: '#dbeafe', color: '#1e40af', fontWeight: 500, fontSize: 11 }}
                />
              ))
            }
          />
          <TextField
            size="small"
            label="HAVING (optional)"
            value={aggHaving}
            onChange={(e) => setAggHaving(e.target.value)}
            fullWidth
            placeholder="e.g., COUNT(order_id) > 5"
            sx={{ '& input': { fontFamily: 'monospace', fontSize: 12 } }}
          />
          {aggExpression && (
            <Box sx={{
              p: 1.5, backgroundColor: '#1e293b', borderRadius: 1,
              fontFamily: 'monospace', fontSize: 12, color: '#e2e8f0', lineHeight: 1.6,
              overflowX: 'auto',
            }}>
              {aggExpression}
            </Box>
          )}
        </>
      )}

      {/* ── Constant Mode ── */}
      {mode === 'constant' && (
        <>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Value Type:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {['String', 'Number', 'Boolean', 'NULL', 'UUID()', 'NOW()'].map((t) => (
              <Chip
                key={t}
                label={t}
                size="small"
                variant={constType === t ? 'filled' : 'outlined'}
                color={constType === t ? 'primary' : 'default'}
                onClick={() => setConstType(t)}
                sx={{ fontSize: 11, cursor: 'pointer' }}
              />
            ))}
          </Box>
          {!['NULL', 'UUID()', 'NOW()'].includes(constType) && (
            <TextField
              size="small"
              label="Constant Value"
              value={constValue}
              onChange={(e) => setConstValue(e.target.value)}
              fullWidth
              placeholder={constType === 'Boolean' ? 'true / false' : 'Enter a fixed value...'}
              sx={{ '& input': { fontFamily: 'monospace', fontSize: 13 } }}
            />
          )}
          {constExpression && (
            <Box sx={{
              p: 1.5, backgroundColor: '#1e293b', borderRadius: 1,
              fontFamily: 'monospace', fontSize: 12, color: '#e2e8f0', lineHeight: 1.6,
            }}>
              {constExpression}
            </Box>
          )}
        </>
      )}

      {/* Create button */}
      <Button
        variant="contained"
        onClick={handleCreate}
        disabled={!canCreate}
        fullWidth
      >
        {createLabel}
      </Button>
    </Stack>
  );
};

export default MappingCreationPanel;
